import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { spellcheckDisabledWithCookieAtom } from '~/atoms';
import { api } from '~/services/api';

import MisspelledNode from './MisspelledNode';
import './SpellCheckerInput.scss';
import SpellCheckerPlugin from './SpellCheckerPlugin';

/**
 * SpellCheckerInput — Rich text input with integrated spell checking.
 *
 * Built on Lexical (a structured text editor framework) because native `<textarea>` and
 * `contentEditable` do not support inline rich annotations (colored underlines, clickable
 * misspelled spans) while preserving undo/redo, cursor position, and copy/paste behavior.
 * Lexical gives us a node tree we can surgically modify (split text, insert styled spans)
 * without destroying the user's editing state.
 *
 * Architecture:
 *   SpellCheckerInput (this file) — public API, Lexical setup, API calls
 *   └─ SpellCheckerPlugin — orchestrates spell check lifecycle, manages tooltip
 *      ├─ MisspelledNode — custom Lexical TextNode with red underline + data-attributes
 *      ├─ SpellCheckerTooltip — suggestion popup (pure React)
 *      └─ utils — node tree helpers, cursor save/restore, offset math
 *
 * Uncontrolled component. Use `defaultValue` for initial content, `onChange` for
 * notifications, and `ref.setValue()` for programmatic updates.
 */

function updateEditorContent(editor, text) {
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();
      const paragraphNode = $createParagraphNode();
      paragraphNode.append($createTextNode(text));
      root.append(paragraphNode);
    },
    { tag: 'spellcheck' } // Prevents the update listener from triggering a spell-check cycle
  );
}

function EditorRefPlugin({ editorRef }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);

  return null;
}

const SpellCheckerInput = forwardRef(
  ({ placeholder = 'Type something...', onChange, textArea = false, defaultValue = '', spellCheckDebounce = 1500, suffix, ...props }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const activeRequestsRef = useRef(0);
    const editorRef = useRef(null);
    const [initialContent] = useState(defaultValue);
    const spellcheckDisabled = useAtomValue(spellcheckDisabledWithCookieAtom);

    useImperativeHandle(
      ref,
      () => ({
        setValue: (text) => {
          if (editorRef.current) {
            updateEditorContent(editorRef.current, text);
          }
        },
      }),
      []
    );

    const checkSpelling = async (text) => {
      activeRequestsRef.current += 1;

      if (activeRequestsRef.current === 1) {
        setIsLoading(true);
      }

      try {
        const response = await api.post('/spellcheck/', {
          body: JSON.stringify({ text }),
        });
        return response.data;
      } catch (error) {
        return [];
      } finally {
        activeRequestsRef.current -= 1;

        if (activeRequestsRef.current === 0) {
          setIsLoading(false);
        }
      }
    };

    const initialConfig = {
      namespace: 'SpellCheckerEditor',
      theme: {
        text: {
          base: 'text-base',
          bold: 'font-bold',
          italic: 'italic',
        },
      },
      onError: (error) => {
        /* eslint-disable */
        console.error('Lexical error:', error);
      },
      nodes: [MisspelledNode],
    };

    const handleTextChange = (editorState) => {
      editorState.read(() => {
        const textContent = $getRoot().getTextContent();
        if (onChange) {
          onChange(textContent);
        }
      });
    };

    return (
      <div className={`spellchecker-wrapper${textArea ? ' textarea' : ''}`}>
        <LexicalComposer
          initialConfig={{
            ...initialConfig,
            editorState: initialContent
              ? () => {
                  const root = $getRoot();
                  const paragraph = $createParagraphNode();
                  paragraph.append($createTextNode(initialContent));
                  root.append(paragraph);
                }
              : undefined,
          }}
        >
          <PlainTextPlugin
            contentEditable={
              <ContentEditable className='spellchecker-input' spellCheck={false} /* Disable native browser spellcheck; we provide our own */ {...props} />
            }
            placeholder={<div className='spellchecker-placeholder body1'>{placeholder}</div>}
          />
          <HistoryPlugin />
          <EditorRefPlugin editorRef={editorRef} />
          {!spellcheckDisabled && <SpellCheckerPlugin checkSpelling={checkSpelling} oneLineMode={!textArea} debounceDelay={spellCheckDebounce} />}
          <OnChangePlugin onChange={handleTextChange} />
        </LexicalComposer>
        {isLoading && <Spin className='absolute spellchecker-loading' />}
        {suffix && <div className='spellchecker-suffix'>{suffix}</div>}
      </div>
    );
  }
);

SpellCheckerInput.displayName = 'SpellCheckerInput';

export default SpellCheckerInput;
